import { useEffect, useRef, useCallback } from 'react';
import { ref, onValue, off } from 'firebase/database';
import { db } from '../../firebaseConfig';
import {
  syncPhase, syncMeta, syncPlayers, syncHand, syncReady,
  syncNemCompleted, syncRevealedArrangements, syncRoundResults,
  syncBetweenRounds, syncDealerSuccession, syncConnection, syncSessionTotals,
  syncRoundHistory,
} from '../gameActions';

export function useFirebaseSync(gameId, uid, dispatch) {
  const listenersRef = useRef([]);

  const subscribe = useCallback((path, callback) => {
    const dbRef = ref(db, path);
    onValue(dbRef, callback);
    listenersRef.current.push(dbRef);
  }, []);

  const cleanup = useCallback(() => {
    listenersRef.current.forEach(r => off(r));
    listenersRef.current = [];
  }, []);

  useEffect(() => {
    if (!gameId || !uid) return;

    const base = `games/${gameId}`;

    // Phase
    subscribe(`${base}/phase`, (snap) => {
      dispatch(syncPhase(snap.val()));
    });

    // Meta
    subscribe(`${base}/meta`, (snap) => {
      const meta = snap.val();
      if (meta) dispatch(syncMeta(meta));
    });

    // Players
    subscribe(`${base}/players`, (snap) => {
      dispatch(syncPlayers(snap.val() || {}));
    });

    // My hand — reducer detects new deal vs Nem by checking phase
    subscribe(`${base}/hands/${uid}`, (snap) => {
      const raw = snap.val();
      const hand = Array.isArray(raw) ? raw : (raw ? Object.values(raw) : null);
      if (hand && hand.length > 0) {
        dispatch(syncHand(hand));
      }
    });

    // Ready status
    subscribe(`${base}/ready`, (snap) => {
      dispatch(syncReady(snap.val() || {}));
    });

    // Nem completed
    subscribe(`${base}/nem/completed`, (snap) => {
      dispatch(syncNemCompleted(snap.val() || {}));
    });

    // Revealed arrangements (showdown)
    subscribe(`${base}/revealedArrangements`, (snap) => {
      const arr = snap.val();
      if (arr) dispatch(syncRevealedArrangements(arr));
    });

    // Round results
    subscribe(`${base}/roundResults`, (snap) => {
      const results = snap.val();
      if (results) dispatch(syncRoundResults(results));
    });

    // Between rounds
    subscribe(`${base}/betweenRounds`, (snap) => {
      const data = snap.val();
      dispatch(syncBetweenRounds({
        ready: data?.ready || {},
        timerStartTime: data?.timerStartTime || null,
      }));
    });

    // Dealer succession
    subscribe(`${base}/dealerSuccession`, (snap) => {
      dispatch(syncDealerSuccession(snap.val()));
    });

    // Session totals
    subscribe(`${base}/sessionTotals`, (snap) => {
      dispatch(syncSessionTotals(snap.val() || {}));
    });

    // Per-round score history (running tally + replay)
    subscribe(`${base}/roundHistory`, (snap) => {
      dispatch(syncRoundHistory(snap.val() || {}));
    });

    // Connection status
    subscribe('.info/connected', (snap) => {
      dispatch(syncConnection(!!snap.val()));
    });

    return cleanup;
  }, [gameId, uid, dispatch, subscribe, cleanup]);

  return { cleanup };
}
